/**
 * [C38] The office backup key in the Super Manager's browser (D-173, D-208, D-209; ADR-05 §2.2). The private key is
 * generated here and never sent to the platform (D-042): only the public key is registered, a test challenge is
 * opened here, and with the optional platform share on (D-208) the key is split 2-of-3 here.
 *
 *  - libsodium is loaded on demand (only the maintenance dialogs need it), the same library as the server (D-201).
 *  - Printed text and shares: `@faylasuf/crypto/key-sheet` (D-355), the same code the server tests.
 *  - Device copy (ADR-05 §2.2 (b), D-362): **only the device's share** when the platform share is on, wrapped with a
 *    non-extractable AES-GCM key in this browser's IndexedDB. [C38 review 5] Same-origin script (devtools on this
 *    browser profile) can still use the wrapping key, so the whole key is never stored here: one share alone reveals
 *    nothing, and the platform's share is released only to the Super Manager with PIN + a fresh code.
 */
import { combineShares, decodeSheet, encodeSheet, shareFromBytes, shareToBytes, splitSecret, type KeyShare } from "@faylasuf/crypto/key-sheet";

type Sodium = typeof import("libsodium-wrappers");
let sodiumReady: Promise<Sodium> | null = null;
async function sodium(): Promise<Sodium> {
  sodiumReady ??= import("libsodium-wrappers").then(async (m) => {
    const s = (m.default ?? m) as Sodium;
    await s.ready;
    return s;
  });
  return sodiumReady;
}

export const randomBytes = (n: number): Uint8Array => crypto.getRandomValues(new Uint8Array(n));
export const toB64 = (b: Uint8Array): string => btoa(String.fromCharCode(...b));
export const fromB64 = (s: string): Uint8Array => Uint8Array.from(atob(s), (c) => c.charCodeAt(0));

/** First 16 bytes of SHA-256(public key), hex — the server's `backupKeyId`. */
export async function keyIdOf(publicKey: Uint8Array): Promise<string> {
  const h = new Uint8Array(await crypto.subtle.digest("SHA-256", publicKey as BufferSource));
  return Array.from(h.subarray(0, 16), (x) => x.toString(16).padStart(2, "0")).join("");
}

export interface BrowserKeyPair {
  readonly publicKey: Uint8Array;
  readonly privateKey: Uint8Array;
  readonly keyId: string;
}

export async function generateKeyPair(): Promise<BrowserKeyPair> {
  const s = await sodium();
  const kp = s.crypto_box_keypair();
  return { publicKey: kp.publicKey, privateKey: kp.privateKey, keyId: await keyIdOf(kp.publicKey) };
}

export async function keyPairFromPrivate(privateKey: Uint8Array): Promise<BrowserKeyPair> {
  const s = await sodium();
  const publicKey = s.crypto_scalarmult_base(privateKey);
  return { publicKey, privateKey, keyId: await keyIdOf(publicKey) };
}

/** Opens the server's D-209 challenge (a sealed box); throws when the key is not the office key. */
export async function openChallenge(sealedB64: string, kp: BrowserKeyPair): Promise<Uint8Array> {
  const s = await sodium();
  return s.crypto_box_seal_open(fromB64(sealedB64), kp.publicKey, kp.privateKey);
}

export const sheetText = {
  key: (privateKey: Uint8Array) => encodeSheet("key", privateKey),
  share: (share: KeyShare) => encodeSheet("share", shareToBytes(share)),
};

/** Reads typed sheet text: the full key, or a share. Throws SHEET_* for a mistyped group. */
export function readSheet(text: string): { kind: "key"; privateKey: Uint8Array } | { kind: "share"; share: KeyShare } {
  const d = decodeSheet(text);
  return d.kind === "key" ? { kind: "key", privateKey: d.payload } : { kind: "share", share: shareFromBytes(d.payload) };
}

export const split2of3 = (privateKey: Uint8Array): KeyShare[] => splitSecret(privateKey, 2, 3, randomBytes);
export const combine = (a: KeyShare, b: KeyShare): Uint8Array => combineShares([a, b]);

// ---- device copy (IndexedDB, non-extractable wrapping key) ----

const DB = "faylasuf-backup-key";
const STORE = "copies";

function idb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const r = indexedDB.open(DB, 1);
    r.onupgradeneeded = () => r.result.createObjectStore(STORE);
    r.onsuccess = () => resolve(r.result);
    r.onerror = () => reject(r.error);
  });
}
async function idbGet<T>(key: string): Promise<T | undefined> {
  const db = await idb();
  return new Promise((resolve, reject) => {
    const r = db.transaction(STORE, "readonly").objectStore(STORE).get(key);
    r.onsuccess = () => resolve(r.result as T | undefined);
    r.onerror = () => reject(r.error);
  });
}
async function idbPut(key: string, value: unknown): Promise<void> {
  const db = await idb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, "readwrite");
    tx.objectStore(STORE).put(value, key);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

interface DeviceCopy {
  readonly keyId: string;
  readonly kind: "key" | "share";
  readonly wrap: CryptoKey;
  readonly iv: Uint8Array;
  readonly data: ArrayBuffer;
}

/** Keeps the device's share on this browser for this office; returns false when the browser refuses. */
export async function saveDeviceCopy(officeCode: string, keyId: string, kind: "share", bytes: Uint8Array): Promise<boolean> {
  try {
    const wrap = await crypto.subtle.generateKey({ name: "AES-GCM", length: 256 }, false, ["encrypt", "decrypt"]);
    const iv = randomBytes(12);
    const data = await crypto.subtle.encrypt({ name: "AES-GCM", iv: iv as BufferSource, additionalData: new TextEncoder().encode(`${officeCode}:${keyId}:${kind}`) }, wrap, bytes as BufferSource);
    await idbPut(officeCode, { keyId, kind, wrap, iv, data } satisfies DeviceCopy);
    return true;
  } catch {
    return false;
  }
}

export async function loadDeviceCopy(officeCode: string, keyId: string): Promise<{ kind: "key"; privateKey: Uint8Array } | { kind: "share"; share: KeyShare } | null> {
  try {
    const c = await idbGet<DeviceCopy>(officeCode);
    if (!c || c.keyId !== keyId) return null;
    const plain = new Uint8Array(await crypto.subtle.decrypt({ name: "AES-GCM", iv: c.iv as BufferSource, additionalData: new TextEncoder().encode(`${officeCode}:${keyId}:${c.kind}`) }, c.wrap, c.data));
    return c.kind === "key" ? { kind: "key", privateKey: plain } : { kind: "share", share: shareFromBytes(plain) };
  } catch {
    return null;
  }
}

/** Removes this office's device copy (the platform share was turned off). */
export async function clearDeviceCopy(officeCode: string): Promise<void> {
  try {
    const db = await idb();
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(STORE, "readwrite");
      tx.objectStore(STORE).delete(officeCode);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  } catch {
    // nothing stored, or storage refused: nothing to remove
  }
}
